import { Component, computed, signal, OnInit, OnDestroy, effect, Output, EventEmitter, inject, ChangeDetectorRef, ElementRef } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { matTooltipSmartImports } from '@app/shared/imports/mat-tooltip-smart';
import { NotificationService, Notification } from '../../../core/services/notifications/notification.service';
import {
  HtmlProcessingService,
  ensureFileNameHasExtension,
  getFileExtensionFromNameOrUrl,
} from '@app/core/services/html-processing/html-processing.service';
import {
  NotificationAttachmentPreviewService,
  NotificationAttachmentKind,
} from '@app/core/services/notification-attachment-preview/notification-attachment-preview.service';
import { SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Spinner } from '../spinner/spinner';
import { Router } from '@angular/router';
import { RelativeTimePipe } from '../pipes/time-pipes';

@Component({
  selector: 'app-notification-dropdown',
  templateUrl: './notification-dropdown.html',
  styleUrls: ['./notification-dropdown.scss'],
  standalone: true,
  imports: [CommonModule, ...matTooltipSmartImports, Spinner, RelativeTimePipe]
})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private router = inject(Router);
  private readonly location = inject(Location);
  private cdr = inject(ChangeDetectorRef);
  private readonly htmlProcessing = inject(HtmlProcessingService);
  readonly attachmentPreview = inject(NotificationAttachmentPreviewService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly descriptionHtmlCache = new Map<string, SafeHtml>();
  private readonly onDropdownClick = (event: MouseEvent) => this.onDescriptionClick(event);

  // Track which notification is being marked as read for animation
  markingAsRead: string | null = null;

  // Track image loading errors
  imageLoadError: { [key: string]: boolean } = {};

  @Output() viewAll = new EventEmitter<void>();
  @Output() notificationClicked = new EventEmitter<void>();
  // Use service signals
  allNotifications = computed(() => this.notificationService.notifications());
  unreadCount = computed(() => this.notificationService.unreadCount());
  isLoading = computed(() => this.notificationService.isLoading());
  isInitialized = computed(() => this.notificationService.isInitialized());

  // Show last 5 notifications regardless of seen/unseen status
  notifications = computed(() => {
    const allNotifs = this.allNotifications();

    // Sort by creation date (newest first)
    const sorted = allNotifs.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });

    // Limit to 5 notifications
    return sorted.slice(0, 5);
  });

  constructor(public notificationService: NotificationService) {
    // Set up effect to track notifications
    effect(() => {
      const notifs = this.notifications();
      const unseenCount = this.notificationService.unseenCount();
      console.log('NotificationDropdownComponent: Notifications:', notifs.length, 'Unseen count:', unseenCount);
      // Force change detection to ensure UI updates
      this.cdr.detectChanges();
    });
  }

  ngOnInit(): void {
    this.host.nativeElement.addEventListener('click', this.onDropdownClick, true);

    // Listen for new notifications
    this.notificationService.newNotification$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notification => {
        console.log('NotificationDropdownComponent: New notification received:', notification);
      });
  }

  ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener('click', this.onDropdownClick, true);
    // Clean up
    this.destroy$.next();
    this.destroy$.complete();
  }

  markAllAsSeen() {
    // Mark all unread notifications as read
    this.notificationService.markAllAsSeen();
  }

  markAsSeen(notification: Notification) {
    // Set the marking state for animation
    this.markingAsRead = notification.id;

    // Add a small delay to show the animation before actually marking as read
    setTimeout(() => {
      this.notificationService.markAsSeen(notification.id);
      this.markingAsRead = null;
    }, 200);
  }

  togglePin(notification: Notification) {
    this.notificationService.togglePin(notification.id);
  }

  readonly notificationsHref = this.location.prepareExternalUrl(
    this.router.serializeUrl(this.router.createUrlTree(['/notifications']))
  );

  viewAllNotifications(): void {
    this.viewAll.emit();
    this.notificationService.markAllAsSeen();
  }

  getSafeDescription(description: string | undefined): SafeHtml {
    const raw = description ?? '';
    const cached = this.descriptionHtmlCache.get(raw);
    if (cached) {
      return cached;
    }

    const safe = this.htmlProcessing.getSafeHtml(this.prepareDescriptionHtml(raw));
    if (this.descriptionHtmlCache.size > 80) {
      const firstKey = this.descriptionHtmlCache.keys().next().value;
      if (firstKey != null) {
        this.descriptionHtmlCache.delete(firstKey);
      }
    }
    this.descriptionHtmlCache.set(raw, safe);
    return safe;
  }

  onDescriptionClick(event: MouseEvent): void {
    const description = (event.target as HTMLElement | null)?.closest?.('.comment-snippet');
    if (!description || !this.host.nativeElement.contains(description)) {
      return;
    }

    const anchor = (event.target as HTMLElement | null)?.closest?.('a');
    if (!anchor || !description.contains(anchor)) {
      return;
    }

    const href = this.usableHref(anchor);
    if (!href) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.handleAttachmentOrLink(anchor, href);
  }

  private handleAttachmentOrLink(anchor: Element, href: string): void {
    const fileName = this.fileNameFromAnchor(anchor, href);
    const preferred = this.preferredKindFromAnchor(anchor);
    const resolved = ensureFileNameHasExtension(fileName, href);
    const kind =
      preferred ||
      this.attachmentPreview.detectKind(resolved, href, fileName);

    if (kind === 'image' || kind === 'media') {
      this.attachmentPreview.openAttachment(href, resolved, kind);
      this.notificationClicked.emit();
      return;
    }

    const ext = getFileExtensionFromNameOrUrl(resolved, href);
    if (ext && !this.isWebPageExtension(ext)) {
      this.attachmentPreview.openAttachment(href, resolved, 'file');
      this.notificationClicked.emit();
      return;
    }

    this.openExternalHref(href);
  }

  private preferredKindFromAnchor(anchor: Element): NotificationAttachmentKind | undefined {
    if (
      anchor.classList.contains('notification-image-chip') ||
      anchor.classList.contains('entry-uploaded-image')
    ) {
      return 'image';
    }
    if (anchor.classList.contains('notification-media-chip')) {
      return 'media';
    }
    return undefined;
  }

  private fileNameFromAnchor(anchor: Element, href: string): string {
    const dataName = (anchor.getAttribute('data-file-name') || '').trim();
    const chipName =
      anchor.querySelector(
        '.notification-image-chip__name, .notification-media-chip__name, .entry-uploaded-image__name'
      )?.textContent || '';
    const title = (anchor.getAttribute('title') || '').trim();
    const text = (anchor.textContent || '').replace(/\s+/g, ' ').trim();
    return (
      dataName ||
      chipName.trim() ||
      title ||
      text ||
      this.basenameFromHref(href) ||
      'download'
    );
  }

  private isWebPageExtension(ext: string): boolean {
    return ['html', 'htm', 'php', 'asp', 'aspx'].includes((ext || '').toLowerCase());
  }

  private usableHref(anchor: Element | null): string {
    if (!anchor) {
      return '';
    }
    const href = (anchor.getAttribute('href') || '').trim();
    if (!href || href === '#' || href.toLowerCase().startsWith('javascript:')) {
      return '';
    }
    return href;
  }

  private openExternalHref(href: string): void {
    const url = this.resolveDescriptionHref(href);
    const opener = document.createElement('a');
    opener.href = url;
    opener.target = '_blank';
    opener.rel = 'noopener noreferrer';
    document.body.appendChild(opener);
    opener.click();
    opener.remove();
  }

  private prepareDescriptionHtml(html: string): string {
    const linked = this.htmlProcessing.linkifyPlainUrlsInHtml(html || '');
    if (typeof DOMParser === 'undefined') {
      return this.htmlProcessing.truncateHtmlByWords(linked, 36);
    }

    try {
      const doc = new DOMParser().parseFromString(linked, 'text/html');

      doc.querySelectorAll('video, iframe, object, embed').forEach((el) => {
        if (el instanceof HTMLVideoElement) {
          const src =
            (el.getAttribute('src') || el.querySelector('source')?.getAttribute('src') || '').trim();
          const name =
            (el.getAttribute('title') || el.getAttribute('aria-label') || '').trim() ||
            this.basenameFromHref(src) ||
            'Video';
          if (src) {
            el.replaceWith(this.createMediaChip(doc, src, name));
            return;
          }
        }
        el.remove();
      });

      doc.querySelectorAll('img').forEach((imgEl) => {
        const img = imgEl as HTMLImageElement;
        const src = (img.getAttribute('src') || '').trim();
        const alt = (img.getAttribute('alt') || '').trim() || this.basenameFromHref(src) || 'Image';
        const chip = this.createImageChip(doc, src || '#', alt);
        const parentAnchor = img.closest('a');
        img.replaceWith(chip);
        if (parentAnchor && parentAnchor !== chip && doc.body.contains(parentAnchor)) {
          const chipHref = this.usableHref(parentAnchor) || this.usableHref(chip) || src;
          if (chipHref) {
            chip.setAttribute('href', this.resolveDescriptionHref(chipHref));
          }
          parentAnchor.replaceWith(...Array.from(parentAnchor.childNodes));
        }
      });

      doc.querySelectorAll('a[href]').forEach((anchor) => {
        if (
          anchor.classList.contains('notification-image-chip') ||
          anchor.classList.contains('notification-media-chip') ||
          anchor.classList.contains('entry-uploaded-image')
        ) {
          return;
        }
        const href = (anchor.getAttribute('href') || '').trim();
        if (!href) {
          return;
        }
        const label = (anchor.textContent || '').replace(/\s+/g, ' ').trim();
        const resolved = ensureFileNameHasExtension(
          label || this.basenameFromHref(href) || 'download',
          href
        );
        if (this.attachmentPreview.detectKind(resolved, href, label) === 'media') {
          anchor.replaceWith(this.createMediaChip(doc, href, resolved));
        }
      });

      doc.querySelectorAll('a[href]').forEach((anchor) => {
        const href = (anchor.getAttribute('href') || '').trim();
        if (!href) {
          return;
        }
        const isChip =
          anchor.classList.contains('notification-image-chip') ||
          anchor.classList.contains('notification-media-chip');
        const textLength = (anchor.textContent || '').replace(/\s+/g, ' ').trim().length;
        const hasBlockKids = !!anchor.querySelector('p, div, li, ul, ol, h1, h2, h3, h4, table');
        if (!isChip && (hasBlockKids || textLength > 80)) {
          const nodes = Array.from(anchor.childNodes);
          const hasChip = nodes.some(
            (n) =>
              n instanceof HTMLElement &&
              (n.classList.contains('notification-image-chip') ||
                n.classList.contains('notification-media-chip'))
          );
          if (hasChip || hasBlockKids) {
            anchor.replaceWith(...nodes);
            return;
          }
        }
        anchor.setAttribute('href', this.resolveDescriptionHref(href));
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('rel', 'noopener noreferrer');
      });

      this.truncateDescriptionDom(doc.body, 36);
      return doc.body.innerHTML;
    } catch {
      return this.htmlProcessing.truncateHtmlByWords(linked, 36);
    }
  }

  private truncateDescriptionDom(root: HTMLElement, wordLimit: number): void {
    let wordsUsed = 0;
    let done = false;
    const toRemove: Node[] = [];

    const visit = (node: Node): void => {
      if (done) {
        toRemove.push(node);
        return;
      }
      if (node.nodeType === Node.TEXT_NODE) {
        const raw = node.textContent || '';
        const parts = raw.split(/(\s+)/);
        let rebuilt = '';
        for (const part of parts) {
          if (!part) {
            continue;
          }
          if (/^\s+$/.test(part)) {
            if (wordsUsed > 0 && wordsUsed < wordLimit) {
              rebuilt += part;
            }
            continue;
          }
          if (wordsUsed >= wordLimit) {
            done = true;
            break;
          }
          wordsUsed += 1;
          rebuilt += part;
        }
        node.textContent = done ? (rebuilt ? `${rebuilt}…` : '…') : rebuilt;
        return;
      }
      if (!(node instanceof HTMLElement)) {
        return;
      }
      if (
        node.classList.contains('notification-image-chip') ||
        node.classList.contains('notification-media-chip')
      ) {
        if (wordsUsed >= wordLimit) {
          toRemove.push(node);
        }
        return;
      }
      Array.from(node.childNodes).forEach(visit);
    };

    Array.from(root.childNodes).forEach(visit);
    toRemove.forEach((n) => n.parentNode?.removeChild(n));
  }

  private createImageChip(doc: Document, href: string, fileName: string): HTMLAnchorElement {
    const resolved = ensureFileNameHasExtension(fileName, href);
    const a = doc.createElement('a');
    a.className = 'notification-image-chip';
    a.setAttribute('href', this.resolveDescriptionHref(href));
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
    a.setAttribute('title', resolved);
    a.setAttribute('data-file-name', resolved);
    const icon = doc.createElement('i');
    icon.className = 'wl_icon_img-icon notification-image-chip__icon';
    icon.setAttribute('aria-hidden', 'true');
    const name = doc.createElement('span');
    name.className = 'notification-image-chip__name';
    name.textContent = this.shortFileName(resolved);
    a.appendChild(icon);
    a.appendChild(name);
    return a;
  }

  private createMediaChip(doc: Document, href: string, fileName: string): HTMLAnchorElement {
    const resolved = ensureFileNameHasExtension(fileName, href);
    const a = doc.createElement('a');
    a.className = 'notification-media-chip';
    a.setAttribute('href', this.resolveDescriptionHref(href));
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
    a.setAttribute('title', resolved);
    a.setAttribute('data-file-name', resolved);
    const name = doc.createElement('span');
    name.className = 'notification-media-chip__name';
    name.textContent = this.shortFileName(resolved);
    a.appendChild(name);
    return a;
  }

  private shortFileName(name: string): string {
    const cleaned = (name || 'File').trim() || 'File';
    if (cleaned.length <= 22) {
      return cleaned;
    }
    const extMatch = cleaned.match(/(\.[a-z0-9]{2,5})$/i);
    const ext = extMatch?.[1] || '';
    const base = ext ? cleaned.slice(0, -ext.length) : cleaned;
    return `${base.slice(0, 16)}…${ext}`;
  }

  private basenameFromHref(href: string): string {
    if (!href) {
      return '';
    }
    try {
      const u = new URL(href, window.location.origin);
      return decodeURIComponent(u.pathname.split('/').filter(Boolean).pop() || '') || '';
    } catch {
      const parts = href.split(/[/?#]/).filter(Boolean);
      return parts.length ? decodeURIComponent(parts[parts.length - 1]) : '';
    }
  }

  private resolveDescriptionHref(href: string): string {
    const trimmed = href.trim();
    if (/^(https?:|mailto:|tel:)/i.test(trimmed)) {
      return trimmed;
    }
    if (trimmed.startsWith('//')) {
      return `${window.location.protocol}${trimmed}`;
    }
    if (trimmed.startsWith('/#/')) {
      return `${window.location.origin}${trimmed}`;
    }
    if (trimmed.startsWith('#/')) {
      return `${window.location.origin}/${trimmed}`;
    }
    if (trimmed.startsWith('/')) {
      return `${window.location.origin}/#${trimmed}`;
    }
    return trimmed;
  }

  /** Object link label (task/issue/request name) — never the bare objCode. */
  getDisplayName(notification: Notification): string {
    if (notification.objectDetails) {
      if (notification.objCode === 'PROJECT' && notification.objectDetails.projectName) {
        return notification.objectDetails.projectName;
      }
      if (notification.objCode === 'TASK' && notification.objectDetails.taskName) {
        return notification.objectDetails.taskName;
      }
      if (notification.objCode === 'PEOPLE' && notification.objectDetails.personName) {
        return notification.objectDetails.personName;
      }
      if (notification.objCode === 'TIMESHEET' && notification.objectDetails.timesheetName) {
        return notification.objectDetails.timesheetName;
      }
      if (notification.objCode === 'REVISION' && notification.objectDetails.revisionName) {
        return notification.objectDetails.revisionName;
      }
      if (notification.objCode === 'REQUEST' && notification.objectDetails.requestName) {
        return notification.objectDetails.requestName;
      }
      if (notification.objCode === 'UPDATES' && notification.objectDetails.updateName) {
        return notification.objectDetails.updateName;
      }
      if (notification.objCode === 'ISSUE') {
        const issueName =
          notification.objectDetails.issueName ||
          notification.objectDetails.name ||
          notification.objectDetails.title ||
          notification.objectname;
        if (issueName && !this.isBareObjCode(issueName, notification.objCode)) {
          return issueName;
        }
      }
      if (notification.objectDetails.name && !this.isBareObjCode(notification.objectDetails.name, notification.objCode)) {
        return notification.objectDetails.name;
      }
      if (notification.objectDetails.title && !this.isBareObjCode(notification.objectDetails.title, notification.objCode)) {
        return notification.objectDetails.title;
      }
    }

    const objectname = String(notification.objectname || '').trim();
    if (objectname && !this.isBareObjCode(objectname, notification.objCode)) {
      return objectname;
    }
    const project = String(notification.project || '').trim();
    if (project && !this.isBareObjCode(project, notification.objCode)) {
      return project;
    }
    return '';
  }

  private isBareObjCode(value: string, objCode?: string): boolean {
    const v = String(value || '').trim().toUpperCase();
    const code = String(objCode || '').trim().toUpperCase();
    return !!v && !!code && v === code;
  }

  // Get initials from name for avatar fallback
  getInitials(name: string): string {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  }

  // Convert text to sentence case (first letter uppercase, rest lowercase)
  toSentenceCase(text: string): string;
  toSentenceCase(text: string | undefined): string;
  toSentenceCase(text: string | undefined): string {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  // Get avatar classes for styling
  getAvatarClasses(notificationId: string, hasAvatar: boolean): Record<string, boolean> {
    const classes: Record<string, boolean> = {
      'user-avatar': true,
      'has-avatar': hasAvatar,
    };

    if (!hasAvatar) {
      // Use notification ID to create consistent color assignment
      const hash = notificationId.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      const paletteIndex = (Math.abs(hash) % 6) + 1;
      classes['avatar-placeholder'] = true;
      classes[`avatar-palette-${paletteIndex}`] = true;
    }

    return classes;
  }

  // Handle image loading errors
  onImageError(notificationId: string): void {
    this.imageLoadError[notificationId] = true;
  }

  // Handle successful image loading
  onImageLoad(notificationId: string): void {
    this.imageLoadError[notificationId] = false;
  }

  onNotificationClick(notification: Notification, event?: Event): void {
    if (event) {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.('a, .comment-snippet a, .notification-image-chip, .notification-media-chip')) {
        return;
      }
      event.stopPropagation();
    }

    // Mark as seen first
    this.notificationService.markAsRead(notification.id);

    // Get route based on notification object details
    const route = this.notificationService.getNotificationRoute(notification);

    if (route) {
      // Get fragment for scrolling to specific update/reply
      const fragment = this.notificationService.getNotificationFragment(notification);

      // Check if route contains query parameters (for timesheet notifications)
      if (Array.isArray(route) && route.length === 2 && typeof route[1] === 'object') {
        // This is a route with query parameters (e.g., ['/timesheets', queryParams])
        const navigationExtras: any = {};

        if (fragment) {
          navigationExtras.fragment = fragment;
        }

        // Add query parameters
        navigationExtras.queryParams = route[1];

        this.router.navigate([route[0]], navigationExtras);
      } else {
        // Regular route navigation
        if (fragment) {
          this.router.navigate(route, { fragment });
        } else {
          this.router.navigate(route);
        }
      }
    }

    // Emit event to notify parent (header) to close the dropdown
    this.notificationClicked.emit();
  }
}
